import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  OnInit
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';

import { waitFor } from '@app/stores/utils/signal-async.utils';
import { ACCESS_STORE } from '@core-interactions/stores/access.store';
import { ENVIRONMENT } from '@env/environment';
import { ClientPowerBIReport } from '@power-bi/models/client-power-bi-report';
import { RouterService } from '@shared/services/router.service';
import { APP_STORE } from '@stores/app.store';
import { Account } from '@stores/models/app/account';
import { AccountType } from '@stores/models/app/account-type';

@Component({
  selector: 'app-client-setup',
  templateUrl: './client-setup.component.html',
  styleUrls: ['./client-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatProgressSpinnerModule]
})
export class ClientSetupComponent implements OnInit {
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #routerService = inject(RouterService);
  readonly #appStore = inject(APP_STORE);
  readonly #accessStore = inject(ACCESS_STORE);
  readonly #injector = inject(Injector);

  ngOnInit(): void {
    void this.init();
  }

  //#region Private methods

  private async init(): Promise<void> {
    const clientIdFromURL =
      this.#activatedRoute.snapshot.queryParamMap.get('clientId');
    const reportIdFromURL =
      this.#activatedRoute.snapshot.queryParamMap.get('reportId');
    let redirectTo =
      this.#activatedRoute.snapshot.queryParamMap.get('redirectTo');

    if (!clientIdFromURL) {
      await this.#router.navigate(['/error'], {
        queryParams: { errorType: 'no-client' }
      });

      this.#appStore.setupClientSuccess(clientIdFromURL ?? '');
      return;
    }

    const selectedAccount = this.getSelectedAccount();
    if (
      selectedAccount.accountType !== AccountType.SystemAdmin &&
      !selectedAccount.client?.modules.includes(ENVIRONMENT.moduleId)
    ) {
      await this.#router.navigate(['/error'], {
        queryParams: { errorType: 'no-module-access' }
      });

      this.#appStore.setupClientSuccess(clientIdFromURL);

      return;
    }

    const reports = await this.loadReports();

    if (this.errorOccurred()) {
      // Redirect is necessary because we can't use onSameUrlNavigation: 'reload' in app-routing
      // (Refresh when navigate to same URL is necessary if we remain on the same page)
      await this.#router.navigate(['/error'], {
        queryParams: { errorType: 'client-setup-failure' }
      });

      this.#appStore.setupClientFailure();
      return;
    }

    if (!reports?.length) {
      await this.#router.navigate(['/error'], {
        queryParams: { errorType: 'no-dashboard' }
      });

      this.#appStore.setupClientSuccess(clientIdFromURL);
      return;
    }

    if (reportIdFromURL && !reports.some(x => x.id === reportIdFromURL)) {
      redirectTo = 'page-not-found';
    }

    const reportId = this.selectReport(reportIdFromURL, reports);

    const routerUrl = redirectTo
      ? `${clientIdFromURL}/${reportId}/${redirectTo}`
      : '';

    this.#routerService.navigateWithReplace(routerUrl);

    this.#appStore.setupClientSuccess(clientIdFromURL);
  }

  private async loadReports(): Promise<ClientPowerBIReport[]> {
    const accountType = this.#appStore.selectedAccount()!.accountType;
    this.#accessStore.loadReports(accountType);

    await waitFor(
      this.#injector,
      () => this.#accessStore.reports().loaded,
      loaded => loaded
    );

    return this.#accessStore.reports().entities;
  }

  private selectReport(
    reportIdFromURL: string | null,
    reports: ClientPowerBIReport[]
  ): string {
    const report =
      reports.find(x => x.id === reportIdFromURL) ||
      reports.find(
        x => x.id === localStorage.getItem('lastSelectedReportId')
      ) ||
      reports[0];

    this.#appStore.selectReport(report.id);

    return report.id;
  }

  private errorOccurred(): boolean {
    return !!this.#accessStore.reports().error;
  }

  private getSelectedAccount(): Account {
    return this.#appStore.selectedAccount()!;
  }

  //#endregion
}
